/**
 * Navigation helper utilities for preserving query parameters
 * across route changes in HashRouter
 */

/**
 * Appends current query parameters to a route path
 * @param route - The destination route path
 * @param searchParams - URLSearchParams object from useSearchParams hook
 * @returns Route with query parameters appended
 *
 * @example
 * const [searchParams] = useSearchParams();
 * const destination = withQueryParams('/preview', searchParams);
 * // Returns: '/preview?product_handle=abc&selected_foam_package=xyz'
 */
export const withQueryParams = (route: string, searchParams: URLSearchParams): string => {
  const queryString = searchParams.toString();
  return queryString ? `${route}?${queryString}` : route;
};

/**
 * Creates a navigation function that preserves query parameters
 * @param navigate - The navigate function from useNavigate hook
 * @param searchParams - URLSearchParams object from useSearchParams hook
 * @returns A function that navigates while preserving query params
 *
 * @example
 * const navigate = useNavigate();
 * const [searchParams] = useSearchParams();
 * const navigateWithParams = createNavigateWithParams(navigate, searchParams);
 * navigateWithParams('/preview', { replace: true });
 */
export const createNavigateWithParams = (
  navigate: (to: string, options?: { replace?: boolean }) => void,
  searchParams: URLSearchParams
) => {
  return (route: string, options?: { replace?: boolean }) => {
    const destination = withQueryParams(route, searchParams);
    navigate(destination, options);
  };
};

